export type OfferedServiceId = "app-development" | "customized-software" | "database-design" | "consulting";

export interface OfferedService {
  readonly id: OfferedServiceId;
  readonly title: string;
  readonly detailsButtonTitle: string;
  readonly subtitle: string;
  readonly tagline: string;
  readonly descriptions: {
    readonly short: string,
    readonly full: string,
  };
  readonly image: {
    readonly url: string,
    readonly intrinsicSize: {
      readonly width: number,
      readonly height: number,
    },
  };
  readonly details: readonly Readonly<OfferedServiceDetails>[];
}

export interface OfferedServiceDetails {
  readonly id: number;
  readonly title: string;
  readonly description: string;
}

export function getOfferedServices(): readonly Readonly<OfferedService>[] {
  return [
    {
      id: "app-development",
      title: $localize`App Entwicklung`,
      detailsButtonTitle: $localize`Mehr zur App Entwicklung`,
      subtitle: $localize`Mobile Apps für eine mobile Welt`,
      tagline: $localize`Wir mobilisieren Ihr Unternehmen`,
      descriptions: {
        short: $localize`Wir konzipieren und entwickeln iOS Applikationen die auf Ihre konkreten Anforderungen abgestimmt und an Ihre Bedürfnisse
        angepasst sind. Egal ob einfache Standalone Apps oder Apps die in eine komplexe Systemumgebung eingebettet werden sollen.`,
        full: $localize`Mobile Applikationen nehmen in unserer schnelllebigen Zeit einen immer größeren Stellenwert ein. Zusätzlich ergeben sich
        durch die Leistungsfähigkeit der aktuellen Endgeräte immer weitere Bereiche, in denen mobile Applikationen einen bestehenden Arbeitsablauf
        verbessern oder neu gestalten können. Dies sowohl in der Kommunikation mit Mitarbeitern und Kunden, als auch bei Apps für Endanwender und
        Privatpersonen. Wir unterstützen Sie bei der Konzeption und Entwicklung von mobilen Applikationen, sowohl im Enterprise- bzw. Unternehmensumfeld,
        als auch bei Apps für Privatanwender. Unser Schwerpunkt liegt hierbei in der Entwicklung nativer Apps, da diese nach wie vor am besten die
        unterschiedlichen Features und Besonderheiten, sowie die verschiedenen Bedienkonzepte der jeweiligen Plattform (iOS / Android / Windows Phone) unterstützen.`,
      },
      image: {
        url: "assets/images/img_app_development.webp",
        intrinsicSize: {
          width: 800,
          height: 400,
        },
      },
      details: [
        {
          id: 1,
          title: $localize`User Experience`,
          description: $localize`Eine mobile App ist ihr Gesicht nach außen. Wir achten auf moderne, einfache Bedienkonzepte die trotzdem die Komplexität
          notwendiger Interaktionen unterstützt und nutzen hierbei auch plattformspezifische Besonderheiten wie MultiTouch, ForceTouch, usw. die der
          Endbenutzer tagtäglich von diversen Applikationen kennt.`,
        },
        {
          id: 2,
          title: $localize`Anbindung von Backend Systemen`,
          description: $localize`Nicht nur im Enterprise Umfeld haben mobile Applikationen häufig eine Anbindung an einen Webservice oder ein
          firmeninternes Backend System um die für die App notwendigen Daten bereitzustellen bzw. zu synchronisieren. Wir unterstützen Sie sowohl
          bei der Konzeption als auch der Entwicklung von notwendigen Schnittstellen und verlieren dabei auch ihre Security Policies nicht aus dem Blick.
          Wir integrieren somit die mobilen Apps nahtlos in Ihre bestehende IT-Landschaft.`,
        },
        {
          id: 3,
          title: $localize`Publikation und Distribution`,
          description: $localize`Egal ob Ihre App nur inhouse verteilt werden soll oder über einen der plattformspezifischen App Stores.
          Die Distribution bzw. Publikation können wir in beiden Fällen gerne für Sie übernehmen oder Sie dabei unterstützen.`,
        },
      ],
    },
    {
      id: "customized-software",
      title: $localize`Individualsoftware`,
      detailsButtonTitle: $localize`Mehr zur Individualsoftware`,
      subtitle: $localize`An Ihre Bedürfnisse angepasst`,
      tagline: $localize`Weil Software einfach passen muss`,
      descriptions: {
        short: $localize`Sie wollen Ihr vorhandenes System erweitern? Sie benötigen eine Schnittstelle zu einem Drittanbieter- oder
        InHouse-System? Sie wollen ein komplett neues Programmpaket entwickeln? Wir sind Ihr kompetenter Partner!`,
        full: $localize`Jedes Unternehmen verfügt über eigene, betriebsspezifische Abläufe und Besonderheiten. Häufig können diese Prozesse
        nicht oder nur unzureichend mit einer Standardsoftware bewältigt werden. Daher entwickeln oder erweitern wir Ihre Individualsoftware,
        damit Ihre Prozesse optimal und mithilfe moderner und leistungsfähiger IT unterstützt werden. Dazu gehören sowohl Backendsysteme und Webservices
        für die Datenhaltung, -verarbeitung und -bereitstellung als auch Frontendsysteme wie Webapplikationen, Thin-Clients oder Standalone Applikationen.`,
      },
      image: {
        url: "assets/images/img_individual_software.webp",
        intrinsicSize: {
          width: 800,
          height: 400,
        },
      },
      details: [
        {
          id: 1,
          title: $localize`Software die zu Ihnen passt`,
          description: $localize`Wir entwickeln Software, die Ihren Anforderungen entspricht und mit der Sie Ihre Arbeitsabläufe optimal bewerkstelligen können.`,
        },
        {
          id: 2,
          title: $localize`Auf Bestehendem aufbauen`,
          description: $localize`Wir passen Ihre vorhandenen Applikationen veränderten Bedingungen an oder erweitern sie nach Ihren Vorgaben.
          Wir schaffen Schnittstellen zu anderen Systemen, damit diese optimal kommunizieren können.`,
        },
        {
          id: 3,
          title: $localize`In vielen Umgebungen zuhause`,
          description: $localize`Wir realisieren sowohl Applikationen und Services im Enterprise Umfeld als auch im Web- und Cloud-Umfeld.
          Dabei nutzen wir aktuelle Technologien und Programmiersprachen und passen uns dabei natürlich Ihren Anforderungen und bestehenden Gegebenheiten an.`,
        },
        {
          id: 4,
          title: $localize`Plattformunabhängig oder Plattformspezifisch?`,
          description: $localize`Soll Ihre Anwendung als plattformunabhängige Webapplikation gestaltet werden die von jedem beliebigen Computer ohne
          vorherige lokale Installation nutzbar ist? Oder bevorzugen Sie die erweiterten Möglichkeiten bezüglich Benutzerinteraktion und
          User-Interfacedesign einer plattformspezifischen Anwendung? Wir sind in beiden Welten zuhause und beraten und unterstützen Sie auch bei der
          Entscheidung, welche Umgebung für Sie am besten passt.`,
        },
      ],
    },
    {
      id: "database-design",
      title: $localize`Datenbankdesign`,
      detailsButtonTitle: $localize`Mehr zum Datenbankdesign`,
      subtitle: $localize`Am Puls der Zeit`,
      tagline: $localize`Ihr komplexes Wissen in einfach strukturierter Form jederzeit verfügbar`,
      descriptions: {
        short: $localize`Aus multidimensionale Unternehmensstrukturen entstehenden vielschichtigen Datenanforderungen. Um schnell und
        zielgerichtet die für Sie relevanten Informationen für den täglichen Bedarf abzufragen wird ein ausgeklügeltes Datenbankdesign benötigt.`,
        full: $localize`Die Leistungsfähigkeit und die Möglichkeiten von Datenbanken erhöhen sich ständig - damit verbunden aber auch die
        Komplexität bei der Entwicklung sowie der Administration. Hier profitieren Sie von unserer langjährigen Erfahrung mit den unterschiedlichen
        Datenbanksystemen, die wir durch Projekte bei diversen namhaften Kunden erworben haben. Egal ob ORACLE, PostgreSQL, MySQL, SQLite oder Microsoft SQL-Server.
        Wir haben mit vielen Datenbanksystemen tiefgreifende Erfahrungen. Hierzu gehören sowohl die performante Verarbeitung und Bereitstellung großer
        Datenmengen sowie die Massendatenverarbeitung, als auch die Anbindung von Echtzeitanwendungen, bei denen es um performante Zugriffe auf die
        verschiedenen Datenbanksysteme geht.`,
      },
      image: {
        url: "assets/images/img_database_design.webp",
        intrinsicSize: {
          width: 800,
          height: 400,
        },
      },
      details: [
        {
          id: 1,
          title: $localize`ORACLE`,
          description: $localize`Seit 1996 sind wir in der Entwicklung mit ORACLE-Datenbanken und ORACLE-Werkzeugen tätig. Dabei haben wir auch diverse
          Weiterbildungsmaßnahmen direkt bei ORACLE absolviert. Immer wenn es um große Datenmengen und performante Verarbeitung geht, ist ORACLE bei
          uns in der engeren Wahl.`,
        },
        {
          id: 2,
          title: $localize`PostgreSQL`,
          description: $localize`Einige unserer eigenen Produkte und Apps basieren auf der Datenbank PostgreSQL. Zudem nutzen wir diese schlanke und
          schnelle Datenbank auch gerne für Kundenprojekte.`,
        },
        {
          id: 3,
          title: $localize`MySQL / MariaDB`,
          description: $localize`In einigen Kundenprojekten setzen wir auf MySQL oder MariaDB als Datenbank.`,
        },
        {
          id: 4,
          title: $localize`SQLite`,
          description: $localize`Gerade wenn es darum geht, eine kleine, leichte Datenbank für beispielsweise eine App zu benutzen, ist SQLite ein
          beliebter Kandidat. Viele unserer eigenen Apps, aber auch Apps, die wir für Kunden entwickelt haben, verwenden SQLite als zentralen Datenspeicher.`,
        },
        {
          id: 5,
          title: $localize`Microsoft SQL-Server`,
          description: $localize`Ob in Azure, der Microsoft Cloud, oder lokal installiert, wir entwickeln Microsoft SQL-Server basierte Applikationen
          für jedes Umfeld und haben auch hier bereits erfolgreiche Kundenprojekte mit dieser Datenbank realisiert.`,
        },
      ],
    },
    {
      id: "consulting",
      title: $localize`Consulting`,
      detailsButtonTitle: $localize`Mehr zum Consulting`,
      subtitle: $localize`Für Sie da`,
      tagline: $localize`Damit Sie sich auf Ihre Kernkompetenzen konzentrieren können`,
      descriptions: {
        short: $localize`Damit Sie den Kopf frei haben und Ihr Unternehmen reibungslos läuft kümmern wir uns um die Randbereiche, die nicht zu
        Ihren Kernkompetenzen gehören. Dabei unterstützen wir Sie beim Systemdesign und dem Projektmanagement. Ebenso übernehmen wir für Sie die Betreuung Ihrer Systeme.`,
        full: $localize`Spezielle Aufgaben benötigen spezielle Kenntnisse bzw. Fähigkeiten. Nicht immer sind in einem Unternehmen die notwendigen
        Spezialisten vorhanden um Aufgaben, die nicht zum Kerngeschäft des Unternehmens gehören, zu bewältigen. In diesen Fällen unterstützen wir Sie
        mit unserer langjährigen Erfahrung bei der Realisierung unterschiedlicher IT-Projekte mit unserem exzellenten IT-Know-How, unabhängig und herstellerneutral.`,
      },
      image: {
        url: "assets/images/img_consulting.webp",
        intrinsicSize: {
          width: 800,
          height: 400,
        },
      },
      details: [
        {
          id: 1,
          title: $localize`Systemanalyse`,
          description: $localize`Wir untersuchen die Stärken und Schwächen ihrer Soft- und Hardware und entwickeln Optimierungsmöglichkeiten.`,
        },
        {
          id: 2,
          title: $localize`Angepasste Lösungen`,
          description: $localize`Wir erstellen ein auf Ihre Bedürfnisse zugeschnittenes Software- oder Datenbank-Konzept - sowohl für die Entwicklung
          von Individualsoftware als auch für die Erweiterung Ihrer Standardapplikationen.`,
        },
        {
          id: 3,
          title: $localize`Projektleitung`,
          description: $localize`Wir erstellen für Sie Lasten- und Pflichtenhefte, übernehmen die Leitung und das Controlling für das Projekt und das
          Entwicklungsteam und kommunizieren mit den Fachbereichen.`,
        },
        {
          id: 4,
          title: $localize`Unterstützung`,
          description: $localize`Wir unterstützen Sie herstellerneutral in allen Fragen der IT. Aufgrund unserer langjährigen IT-Erfahrung können wir
          Ihnen neue Perspektiven aufzeigen oder neue Ideen entwickeln.`,
        },
      ],
    },
  ];
}
